import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, TrendingUp } from 'lucide-react';
import type { Line } from '../types/Line';
import { createLine } from '../types/Line';
interface LineManagementProps {
  onBack: () => void;
}
export function LineManagement({
  onBack
}: LineManagementProps) {
  const [lines, setLines] = useState<Line[]>([{
    id: '1',
    lineNumber: 'L-001',
    lineName: 'Linie 1',
    createdAt: new Date('2024-01-15')
  }, {
    id: '2',
    lineNumber: 'L-002',
    lineName: 'Linie 2',
    createdAt: new Date('2024-02-20')
  }, {
    id: '3',
    lineNumber: 'L-003',
    lineName: 'Linie 3',
    createdAt: new Date('2024-03-10')
  }]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedLine, setSelectedLine] = useState<Line | null>(null);
  const [formData, setFormData] = useState({
    lineNumber: '',
    lineName: ''
  });
  const resetForm = () => {
    setFormData({
      lineNumber: '',
      lineName: ''
    });
  };
  const handleAddLine = () => {
    if (formData.lineNumber && formData.lineName) {
      const newLine = createLine(formData.lineNumber, formData.lineName);
      setLines([...lines, newLine]);
      setShowAddModal(false);
      resetForm();
    }
  };
  const handleEditClick = (line: Line) => {
    setSelectedLine(line);
    setFormData({
      lineNumber: line.lineNumber,
      lineName: line.lineName
    });
    setShowEditModal(true);
  };
  const handleUpdateLine = () => {
    if (selectedLine && formData.lineNumber && formData.lineName) {
      setLines(lines.map(l => l.id === selectedLine.id ? {
        ...l,
        lineNumber: formData.lineNumber,
        lineName: formData.lineName
      } : l));
      setShowEditModal(false);
      setSelectedLine(null);
      resetForm();
    }
  };
  const handleDeleteClick = (line: Line) => {
    setSelectedLine(line);
    setShowDeleteConfirm(true);
  };
  const handleConfirmDelete = () => {
    if (selectedLine) {
      setLines(lines.filter(l => l.id !== selectedLine.id));
      setShowDeleteConfirm(false);
      setSelectedLine(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setSelectedLine(null);
  };
  return <div className="space-y-6" data-id="element-102">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-103">
        <h2 className="text-2xl font-semibold text-gray-900" data-id="element-104">
          Linienverwaltung
        </h2>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors" data-id="element-105">
          <Plus className="w-4 h-4" strokeWidth={2} data-id="element-106" />
          Linie hinzufügen
        </button>
      </div>

      {/* Lines List */}
      <div className="space-y-3" data-id="element-107">
        {lines.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200" data-id="element-108">
            <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-3" strokeWidth={1.5} data-id="element-109" />
            <p className="text-gray-500" data-id="element-110">Noch keine Linien angelegt</p>
          </div> : lines.map(line => <motion.div key={line.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-111">
              <div className="flex items-center justify-between" data-id="element-112">
                <div className="flex items-center gap-4" data-id="element-113">
                  <div className="p-3 bg-orange-100 rounded-lg" data-id="element-114">
                    <TrendingUp className="w-5 h-5 text-orange-600" strokeWidth={2} data-id="element-115" />
                  </div>
                  <div data-id="element-116">
                    <h3 className="font-semibold text-gray-900" data-id="element-117">
                      {line.lineName}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-118">
                      Linien-Nr: {line.lineNumber}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2" data-id="element-119">
                  <button onClick={() => handleEditClick(line)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Bearbeiten" data-id="element-120">
                    <Edit2 className="w-4 h-4" strokeWidth={2} data-id="element-121" />
                  </button>
                  <button onClick={() => handleDeleteClick(line)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" aria-label="Löschen" data-id="element-122">
                    <Trash2 className="w-4 h-4" strokeWidth={2} data-id="element-123" />
                  </button>
                </div>
              </div>
            </motion.div>)}
      </div>

      {/* Add Line Modal */}
      <AnimatePresence data-id="element-124">
        {showAddModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={() => {
          setShowAddModal(false);
          resetForm();
        }} data-id="element-125" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-126">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="w-full max-w-md" data-id="element-127">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-128">
                  <div className="p-6" data-id="element-129">
                    <div className="flex justify-between items-start mb-6" data-id="element-130">
                      <h3 className="text-2xl font-semibold text-gray-900" data-id="element-131">
                        Linie hinzufügen
                      </h3>
                      <button onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-132">
                        <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-133" />
                      </button>
                    </div>

                    <div className="space-y-4" data-id="element-134">
                      <div data-id="element-135">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-136">
                          Liniennummer
                        </label>
                        <input type="text" value={formData.lineNumber} onChange={e => setFormData({
                      ...formData,
                      lineNumber: e.target.value
                    })} placeholder="L-001" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-137" />
                      </div>

                      <div data-id="element-138">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-139">
                          Linienname
                        </label>
                        <input type="text" value={formData.lineName} onChange={e => setFormData({
                      ...formData,
                      lineName: e.target.value
                    })} placeholder="Linie 1" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-140" />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6" data-id="element-141">
                      <button onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-142">
                        Abbrechen
                      </button>
                      <button onClick={handleAddLine} disabled={!formData.lineNumber || !formData.lineName} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-143">
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Edit Line Modal */}
      <AnimatePresence data-id="element-144">
        {showEditModal && selectedLine && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={() => {
          setShowEditModal(false);
          setSelectedLine(null);
          resetForm();
        }} data-id="element-145" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-146">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="w-full max-w-md" data-id="element-147">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-148">
                  <div className="p-6" data-id="element-149">
                    <div className="flex justify-between items-start mb-6" data-id="element-150">
                      <h3 className="text-2xl font-semibold text-gray-900" data-id="element-151">
                        Linie bearbeiten
                      </h3>
                      <button onClick={() => {
                    setShowEditModal(false);
                    setSelectedLine(null);
                    resetForm();
                  }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-152">
                        <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-153" />
                      </button>
                    </div>

                    <div className="space-y-4" data-id="element-154">
                      <div data-id="element-155">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-156">
                          Liniennummer
                        </label>
                        <input type="text" value={formData.lineNumber} onChange={e => setFormData({
                      ...formData,
                      lineNumber: e.target.value
                    })} placeholder="L-001" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-157" />
                      </div>

                      <div data-id="element-158">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-159">
                          Linienname
                        </label>
                        <input type="text" value={formData.lineName} onChange={e => setFormData({
                      ...formData,
                      lineName: e.target.value
                    })} placeholder="Linie 1" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-160" />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6" data-id="element-161">
                      <button onClick={() => {
                    setShowEditModal(false);
                    setSelectedLine(null);
                    resetForm();
                  }} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-162">
                        Abbrechen
                      </button>
                      <button onClick={handleUpdateLine} disabled={!formData.lineNumber || !formData.lineName} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-163">
                        Speichern
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence data-id="element-164">
        {showDeleteConfirm && selectedLine && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={handleCancelDelete} data-id="element-165" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-166">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="w-full max-w-md" data-id="element-167">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-168">
                  <div className="p-6" data-id="element-169">
                    <div className="flex justify-between items-start mb-4" data-id="element-170">
                      <h3 className="text-2xl font-semibold text-gray-900" data-id="element-171">
                        Linie löschen?
                      </h3>
                      <button onClick={handleCancelDelete} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-172">
                        <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-173" />
                      </button>
                    </div>

                    <p className="text-gray-600 mb-6" data-id="element-174">
                      Möchten Sie die Linie{' '}
                      <strong data-id="element-175">{selectedLine.lineName}</strong> wirklich löschen?
                    </p>

                    <div className="flex gap-3" data-id="element-176">
                      <button onClick={handleCancelDelete} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-177">
                        Abbrechen
                      </button>
                      <button onClick={handleConfirmDelete} className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors" data-id="element-178">
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>
    </div>;
}